/**
 * Hardened controller for the small "Optional Add-Ons" box.
 * - Rebinds buttons (removes any previous inline listeners) to avoid duplicate requests.
 * - Adds loading guards to prevent double-click races.
 * - Keeps button state in sync with the real cart (including when remove misses).
 * - Re-inits on section reloads.
 */
(function () {
  const qs = (root, sel) => (root || document).querySelector(sel);
  const qsa = (root, sel) => Array.from((root || document).querySelectorAll(sel));

  const FETCH_OPTS = { headers: { Accept: "application/json" } };
  const LOADING_CLASS = "addon-loading";

  let cartSyncPromise = null;

  function parseVariantId(button) {
    const raw = button.getAttribute("data-variant-id");
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }

  function setButtonState(button, { loading = false, inCart = false } = {}) {
    if (!button) return;

    if (loading) {
      button.dataset.loading = "true";
      button.classList.add(LOADING_CLASS);
      button.disabled = true;
      button.textContent = "Working…";
      return;
    }

    button.dataset.loading = "false";
    button.classList.remove(LOADING_CLASS);
    button.disabled = false;

    if (inCart) {
      button.classList.add("remove-state");
      button.textContent = "Remove";
    } else {
      button.classList.remove("remove-state");
      button.textContent = "Add to Cart";
    }
  }

  function fetchCart() {
    // Avoid dog-piling cart.js
    if (!cartSyncPromise) {
      cartSyncPromise = fetch("/cart.js", FETCH_OPTS)
        .then((res) => {
          if (!res.ok) throw new Error("Cart fetch failed");
          return res.json();
        })
        .finally(() => {
          cartSyncPromise = null;
        });
    }
    return cartSyncPromise;
  }

  function syncButtons(cart) {
    const items = (cart && cart.items) || [];
    qsa(document, ".custom-addon-button").forEach((button) => {
      const variantId = parseVariantId(button);
      if (!variantId) {
        setButtonState(button, { inCart: false });
        return;
      }
      const isInCart = items.some((item) => item.id === variantId);
      setButtonState(button, { inCart: isInCart });
    });
  }

  function rebindButton(button) {
    // Drop any existing listeners by cloning
    const cloned = button.cloneNode(true);
    button.replaceWith(cloned);
    return cloned;
  }

  function attachHandlers(container) {
    const buttons = qsa(container || document, ".custom-addon-button");
    if (!buttons.length) return;

    buttons.forEach((button) => {
      if (button.dataset.addonBound === "true") return;
      button.dataset.addonBound = "true";

      const cleanBtn = rebindButton(button);
      cleanBtn.dataset.addonBound = "true";

      cleanBtn.addEventListener("click", (event) => {
        event.preventDefault();

        if (cleanBtn.dataset.loading === "true") return;
        const variantId = parseVariantId(cleanBtn);
        if (!variantId) return;

        const isRemove = cleanBtn.classList.contains("remove-state");
        setButtonState(cleanBtn, { loading: true });

        const afterSync = () =>
          fetchCart()
            .then(syncButtons)
            .catch((err) => console.error("Addon cart sync error:", err))
            .finally(() => setButtonState(cleanBtn));

        if (isRemove) {
          fetchCart()
            .then((cart) => {
              const match = (cart.items || []).find((item) => item.id === variantId);
              if (!match) {
                // Nothing to remove; just resync UI state
                return null;
              }

              // Primary: remove by line-item key
              return fetch("/cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({ id: match.key, quantity: 0 }),
              }).then((res) => {
                if (res.ok) return res.json();

                // Fallback: remove by variant id in case the key is rejected
                return fetch("/cart/update.js", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                  body: JSON.stringify({ updates: { [variantId]: 0 } }),
                }).then((fallbackRes) => {
                  if (!fallbackRes.ok) throw new Error("Remove failed");
                  return fallbackRes.json();
                });
              });
            })
            .then(() => afterSync())
            .catch((err) => {
              console.error("Addon remove error:", err);
              alert("Could not remove this item. Please try again.");
              setButtonState(cleanBtn);
              // Re-sync to recover if we exited early
              fetchCart().then(syncButtons).catch(() => {});
            });
          return;
        }

        // Add
        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
          .then((res) => {
            if (!res.ok) throw new Error("Add failed");
            return res.json();
          })
          .then(() => afterSync())
          .catch((err) => {
            console.error("Addon add error:", err);
            alert("Could not add this item. Please try again.");
            setButtonState(cleanBtn);
            fetchCart().then(syncButtons).catch(() => {});
          });
      });
    });
  }

  function initOptionalAddons(root) {
    const box = qs(root || document, ".custom-addons-box");
    if (!box) return;
    attachHandlers(root);
    fetchCart().then(syncButtons).catch((err) => console.error(err));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initOptionalAddons(), { once: true });
  } else {
    initOptionalAddons();
  }

  document.addEventListener("shopify:section:load", (evt) => {
    initOptionalAddons(evt.target || document);
  });
})();
